import React from 'react'
import { Slider, Text, View } from '@/components'
import { TypeGuards } from '@codeleap/types'
import { createLog } from '@/utils'

type TrackMarkStyle = {
  fontSize?: number
  color?: string
  fontWeight?: number | string
}

type SliderOnboardingProps = {
  label?: string
  labelVariants?: string[]
  min: number
  max: number
  trackMarks?: Record<number, string>
  trackMarkStyle?: TrackMarkStyle
  value?: number
  defaultValue?: number
  onValueChange?: (value: number | number[]) => void
}

const DEBUG = false
const log = createLog('SliderOnboarding', DEBUG)

export function SliderOnboarding(props: SliderOnboardingProps) {
  const { label, min, max, value, defaultValue = 3, onValueChange, trackMarkStyle, ...rest } = props

  const customTrackMarkStyles = React.useMemo(
    () => ({
      trackMark: {
        fontSize: trackMarkStyle?.fontSize,
        color: trackMarkStyle?.color,
        fontWeight: trackMarkStyle?.fontWeight
      }
    }),
    [trackMarkStyle]
  )

  const handleValueChange = React.useCallback(
    (newValue: number | number[]) => {
      log('Slider value changed', { newValue, previousValue: value, min, max })
      if (onValueChange) {
        onValueChange(newValue)
      }
    },
    [onValueChange, value, min, max]
  )

  React.useEffect(() => {
    if (TypeGuards.isNil(value)) {
      log('Value changed via useEffect', { value, min, max, defaultValue })
      handleValueChange(defaultValue)
    }
  }, [handleValueChange])

  const steps = Array.from({ length: max - min + 1 }, (_, i) => i + min)

  if (min == null || max == null) return null
  return (
    <View variants={['fullWidth', 'column']}>
      {label && <Text variants={['p2', 'color:neutral5', 'marginBottom:4']} text={label} />}

      <View variants={['row', 'justifySpaceBetween', 'paddingHorizontal:1.25']}>
        {steps.map(n => (
          <Text key={n} variants={['p4', 'color:neutral9']} text={String(n)} />
        ))}
      </View>

      <Slider
        {...rest}
        value={[value ?? defaultValue]}
        onValueChange={handleValueChange}
        min={min}
        max={max}
        step={1}
        debugName="onboarding-slider"
        variants={['onboarding']}
        styles={customTrackMarkStyles}
      />
    </View>
  )
}
